import { useCallback } from 'react';
import type { LatLng } from '~/_shared/types/latLng';
import { useAddDataRow } from '../../../data/addDataRow/useAddDataRow';
import { useClientIdSelector } from '../../../store/selectors/useClientIdSelector';
import { usePrimarySpreadsheetId } from '../../../store/selectors/usePrimarySpreadsheetId';
import { type GeoLocation } from '../../utils/geolocation/geolocation';
import { getAddressFromLatLng } from '../../utils/geolocation/geolocation.helpers';
import { useGetMarkerDataFromGeoResponse } from '../../utils/geolocation/useGetMarkerDataFromGeoResponse.hook';
import { useTranslation } from '../../utils/hooks';

export const useLocationFromGeolocation = () => {
  const clientId = useClientIdSelector();
  const addMarkerCallback = useGetMarkerDataFromGeoResponse();
  const spreadsheetId = usePrimarySpreadsheetId();
  const { addNewDataRowMapPage } = useAddDataRow();
  const [t] = useTranslation();

  const getGeolocationFromLatLng = useCallback((props: {
    latLng: LatLng;
    callback: (response: GeoLocation) => void;
    onIsLoadingChanged?: (loading: boolean) => void;
  }) => {
    props.onIsLoadingChanged?.(true);
    getAddressFromLatLng(props.latLng.lat, props.latLng.lng).then(response => {
      props.onIsLoadingChanged?.(false);
      props.callback(response);
    }).catch(() => {
      props.onIsLoadingChanged?.(false);
      props.callback({
        address: t('Unknown Location'),
        latLng: { lat: props.latLng.lat, lng: props.latLng.lng },
      });
    });
  }, [t]);

  const addLocationFromLatLng = useCallback((props: {
    latLng: LatLng;
    onIsLoadingChanged?: (loading: boolean) => void;
    onIsGeolocationFromLatLngLoadingChanged?: (loading: boolean) => void;
  }) => {
    if (!addMarkerCallback) {
      return;
    }
    props.onIsLoadingChanged?.(true);

    getGeolocationFromLatLng({
      latLng: props.latLng,
      callback: (response: GeoLocation) => {
        if (spreadsheetId && clientId) {
          const columnValuesForNewDataRow = addMarkerCallback(response, spreadsheetId);
          addNewDataRowMapPage({
            clientId,
            virtualSpreadsheetId: spreadsheetId,
            columnValues: columnValuesForNewDataRow || {},
            lat: props.latLng.lat, lng: props.latLng.lng,
          }, (newIsLoadingValue) => {
            if (!newIsLoadingValue && props.onIsLoadingChanged) {
              props.onIsLoadingChanged(false);
            }
          });
        }
      },
      onIsLoadingChanged: props.onIsGeolocationFromLatLngLoadingChanged,
    });
  }, [clientId, spreadsheetId, addNewDataRowMapPage,
    addMarkerCallback, getGeolocationFromLatLng,
  ]);

  return { getGeolocationFromLatLng, addLocationFromLatLng };
};
